import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { images } from '../../data/mock';
import {
  Search,
  Target,
  FileCheck,
  Mountain,
  Building2,
  Waves,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';

const ServicesPage = () => {
  const { t, isRTL } = useLanguage();

  const services = [
    {
      icon: Target,
      title: t('services.service1Title'),
      objective: t('services.service1Objective'),
      desc: t('services.service1Desc'),
      benefitTitle: t('services.service1BenefitTitle'),
      benefitDesc: t('services.service1BenefitDesc'),
      color: 'red',
    },
    {
      icon: Search,
      title: t('services.service2Title'),
      objective: t('services.service2Objective'),
      desc: t('services.service2Desc'),
      benefitTitle: t('services.service2BenefitTitle'),
      benefitDesc: t('services.service2BenefitDesc'),
      color: 'amber',
    },
    {
      icon: FileCheck,
      title: t('services.service3Title'),
      objective: null,
      desc: t('services.service3Desc'),
      benefitTitle: null,
      benefitDesc: null,
      color: 'emerald',
    },
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('services.pageTitle')}
            </h1>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group p-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl hover:border-red-500/30 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-500/20 transition-colors">
                  <service.icon className="h-7 w-7 text-red-500" />
                </div>

                <h3 className="text-xl font-bold text-white mb-4">
                  {service.title}
                </h3>

                {service.objective && (
                  <p className="text-red-400 text-sm font-medium mb-4">
                    {service.objective}
                  </p>
                )}

                <p className="text-gray-400 leading-relaxed mb-6">
                  {service.desc}
                </p>

                {service.benefitTitle && (
                  <div className="pt-4 border-t border-zinc-800">
                    <p className="text-gray-300 text-sm">
                      <span className="text-green-400 font-medium">
                        {service.benefitTitle}
                      </span>
                      {service.benefitDesc}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Environments */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">
            {t('services.service4Title')}
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Urban */}
            <div className="relative overflow-hidden rounded-2xl">
              <div className="absolute inset-0">
                <img
                  src={images.servland}
                  alt="Urban exploration"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
              </div>
              <div className="relative z-10 p-10 min-h-[350px] flex flex-col justify-end">
                <div className="w-14 h-14 bg-red-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6">
                  <Building2 className="h-7 w-7 text-red-400" />
                </div>
                {/* Per-request: overlay text removed; combined paragraph will appear below both images */}
              </div>
            </div>

            {/* Marine */}
            <div className="relative overflow-hidden rounded-2xl">
              <div className="absolute inset-0">
                <img
                  src={images.offshore}
                  alt="Marine surveys"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
              </div>
              <div className="relative z-10 p-10 min-h-[350px] flex flex-col justify-end">
                <div className="w-14 h-14 bg-blue-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6">
                  <Waves className="h-7 w-7 text-blue-400" />
                </div>
                {/* Per-request: overlay text removed; combined paragraph will appear below both images */}
              </div>
            </div>
          </div>
          {/* Combined paragraph under both images */}
          <div className="mt-8 max-w-4xl mx-auto">
            <p className="text-center text-gray-200 text-lg sm:text-xl font-semibold">
              {t('services.service4Combined')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">
            {t('cta.title')}
          </h2>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/25"
            >
              {t('cta.button')}
              <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          </Link>
        </div>
      </section> */}
    </div>
  );
};

export default ServicesPage;